import { EventEmitter } from '@alphahub/eie-core';
import type { WebFaceDetector } from '../WebFaceDetector';
import type { WebPoseDetector } from '../WebPoseDetector';
import type { WebHandDetector } from '../WebHandDetector';
import type { BaselineCalibrator } from './BaselineCalibrator';

type Getters = {
  getFaceDetector: () => WebFaceDetector | undefined;
  getPoseDetector: () => WebPoseDetector | undefined;
  getHandDetector: () => WebHandDetector | undefined;
  getBaselineCalibrator: () => BaselineCalibrator | undefined;
  isRunning: () => boolean;
};

type Setters = {
  setLatestLandmarks: (landmarks?: any[]) => void;
  setLatestBlendshapes: (bs?: Record<string, number>) => void;
  setLatestHandLandmarks: (landmarks?: any[]) => void;
  setLastValidFaceDetectionTime: (ts: number) => void;
  pushTimelineState: (s: any) => void;
  pushPoseSnapshot: (p: any) => void;
};

const DETECT_INTERVAL = 200; // ms — 5 detections/s

/**
 * UltraDetectionLoop is the single coordinated loop for ultra mode.
 * Replaces TrackingService (60fps RAF) + SnapshotAccumulator (5s setInterval)
 * + BaselineCalibrator polling (100ms setInterval) with one timer that:
 *   - Every 200ms: face detect → update blendshapes/landmarks → feed baseline calibrator
 *   - Every `snapshotInterval` ms: take full snapshot (pose + hand + timeline push)
 */
export class UltraDetectionLoop {
  private intervalId?: number;
  private frameCount = 0;
  private nullCount = 0;
  private lastSnapshotTime = 0;
  private latestBlendshapes?: Record<string, number>;
  private latestLandmarks?: any[];

  constructor(
    private snapshotInterval: number,
    private emitter: EventEmitter,
    private getters: Getters,
    private setters: Setters,
  ) {}

  start(): void {
    if (this.intervalId) return;
    this.lastSnapshotTime = Date.now();

    this.intervalId = window.setInterval(() => {
      if (!this.getters.isRunning()) return;
      this.tick();
    }, DETECT_INTERVAL) as unknown as number;

    // First tick immediately
    this.tick();
  }

  private async tick(): Promise<void> {
    const faceDetector = this.getters.getFaceDetector();
    if (!faceDetector) return;

    // ── 1. Face detection (single call per tick) ──
    try {
      const state = await faceDetector.detect();
      if (state?.faceLandmarks) {
        this.nullCount = 0;
        this.latestLandmarks = state.faceLandmarks;
        this.setters.setLatestLandmarks(state.faceLandmarks);
        if (state.blendshapes) {
          this.latestBlendshapes = state.blendshapes;
          this.setters.setLatestBlendshapes(state.blendshapes);
        }
        this.setters.setLastValidFaceDetectionTime(Date.now());
        this.emitter.emit('faceLandmarks', state.faceLandmarks);
        if (this.frameCount++ % 25 === 0) {
          console.log('🎯 Ultra tick', this.frameCount, 'bs:', Object.keys(state.blendshapes || {}).length);
        }
      } else {
        if (this.nullCount++ % 50 === 0) {
          console.warn('👻 Ultra: no face, null count:', this.nullCount);
        }
      }
    } catch {
      // swallow detector errors
    }

    // ── 2. Feed baseline calibrator (every tick, calibrator decides internally) ──
    const calibrator = this.getters.getBaselineCalibrator();
    if (calibrator) {
      calibrator.feedSample();
    }

    // ── 3. Snapshot accumulation at emotion interval ──
    const now = Date.now();
    if (now - this.lastSnapshotTime >= this.snapshotInterval) {
      this.lastSnapshotTime = now;
      this.takeSnapshot();
    }
  }

  private takeSnapshot(): void {
    const state: any = {
      emotion: 'neutral',
      valence: 0,
      arousal: 0,
      confidence: this.latestBlendshapes ? 0.8 : 0,
      timestamp: Date.now(),
      blendshapes: this.latestBlendshapes ? { ...this.latestBlendshapes } : undefined,
      faceLandmarks: this.latestLandmarks ? [...this.latestLandmarks] : undefined,
    };

    // Hand detection
    let handData: any;
    const handDetector = this.getters.getHandDetector();
    if (handDetector) {
      try { handData = handDetector.detect(); } catch { handData = undefined; }
      if (handData?.landmarks) this.setters.setLatestHandLandmarks(handData.landmarks);
    }

    // Pose detection
    const poseDetector = this.getters.getPoseDetector();
    if (poseDetector) {
      try {
        const pose = poseDetector.detect();
        if (pose) {
          state.bodyPose = { keypoints: pose.keypoints };
          const poseSnap: any = {
            timestamp: pose.timestamp,
            keypoints: pose.keypoints,
            worldLandmarks: pose.worldLandmarks,
          };
          if (handData?.landmarks) poseSnap.handLandmarks = handData.landmarks;
          this.setters.pushPoseSnapshot(poseSnap);
        }
      } catch {}
    }

    this.setters.pushTimelineState(state);
  }

  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }
}
